import device from 'current-device';
import { hasDismissSplashScreen, getBrowserLang } from './util';
import { getGDPRCookie, getUniqueInteractionCookie, setUniqueInteractionCookie, getPasswordFromCookie } from './cookieUtil';
import GDPRConsentLocation from '../model/gdprConsentLocation';
import { DISCLAIMER_TYPE, ConsentGDPRCookieValue } from './constants';

export const isExplicitGDPR = consentLocation => consentLocation && consentLocation === GDPRConsentLocation.EXPLICIT;
export const EUCountryList = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'CH', 'GB', 'US'];

export const hasPasswordInCookie = (isPasswordRequired, isExpired) => {
    const { password } = getPasswordFromCookie(isPasswordRequired, isExpired);
    const passwordInCookie = !!password;
    return passwordInCookie;
};

const hasGDPRInCookie = (gdprSetting, isLoadScriptSuccess, isWatermarkEnabled) => {
    let hasGDPRCookie = false;
    if (isExplicitGDPR(gdprSetting && gdprSetting.consentLocation) && isLoadScriptSuccess) {
        hasGDPRCookie = window.mscc && window.mscc.hasConsent();
    } else {
        const gdprCookie = getGDPRCookie(isWatermarkEnabled);
        hasGDPRCookie = gdprCookie === ConsentGDPRCookieValue.ALL_TRACKING_AND_COOKIES ||
            gdprCookie === ConsentGDPRCookieValue.ONLY_FUNCTIONAL_COOKIES;
    }

    return hasGDPRCookie;
};

export const isPartialGDPROn = (gdprSetting, viewerLocation) => {
    if (!gdprSetting) {
        return true;
    }

    const consentLocation = gdprSetting.consentLocation;
    if (consentLocation === GDPRConsentLocation.PARTIAL) {
        const countryCode = viewerLocation && viewerLocation.countryCode;
        const consentLocationCustom = gdprSetting.consentLocationCustom;
        if (Array.isArray(consentLocationCustom) && countryCode) {
            const isViewerLocationLocateInConsentLocation = consentLocationCustom.some(location => location && location.toUpperCase() === countryCode.toUpperCase());
            // By default, we will open GDPR for the user if the user is not in Europe countries
            const isViewerLocationNoInEU = !EUCountryList.some(location => location.toUpperCase() === countryCode.toUpperCase());
            return isViewerLocationLocateInConsentLocation || isViewerLocationNoInEU;
        }

        return false;
    }

    return false;
};

export const isGDPROn = (gdprSetting, viewerLocation, privacySetting) => {
    if (privacySetting) {
        return privacySetting.isEnable;
    }

    if (!gdprSetting) {
        return true;
    }

    // add for global gdpr
    if (gdprSetting.isEnableGlobalGDPR) {
        return true;
    }

    const consentLocation = gdprSetting.consentLocation;

    if (consentLocation === GDPRConsentLocation.NO_ONE) {
        return false;
    }

    if (consentLocation === GDPRConsentLocation.EVERY_ONE || consentLocation === GDPRConsentLocation.EXPLICIT) {
        return true;
    }

    return isPartialGDPROn(gdprSetting, viewerLocation);
};

export const isSkipSplashScreen = (gdprSetting, isLoadScriptSuccess, isPasswordRequired, isExpired, viewerLocation, privacySetting, humanEvent, isWatermarkEnabled) => {
    if (isExpired) {
        return true;
    }

    if (hasDismissSplashScreen(isPasswordRequired)) {
        return true;
    }

    const isMobileDevice = device.mobile() || device.tablet();
    const isLikelyHuman = humanEvent.detected || isMobileDevice; // treat mobile device as human
    const hasInteractionCookie = getUniqueInteractionCookie();
    const notRobot = isLikelyHuman || hasInteractionCookie;

    const isNoneDisclaimer = privacySetting && privacySetting.isEnable && privacySetting.currentCountry.disclaimerLibrary.type === DISCLAIMER_TYPE.NONE_DISCLAIMER;
    const hasPwdInCookie = hasPasswordInCookie(isPasswordRequired, isExpired);

    if (notRobot) {
        // Treat none disclaimer as GDPR off
        if (!isGDPROn(gdprSetting, viewerLocation, privacySetting) || isNoneDisclaimer) {
            if (isLikelyHuman && !hasInteractionCookie) {
                setUniqueInteractionCookie();
            }

            if (!isPasswordRequired) {
                return true;
            }

            if (isPasswordRequired && hasPwdInCookie) {
                return true;
            }
        } else {
            const hasGDPRCookie = hasGDPRInCookie(gdprSetting, isLoadScriptSuccess, isWatermarkEnabled);
            if (!isPasswordRequired && hasGDPRCookie) {
                return true;
            }

            if (isPasswordRequired && hasPwdInCookie && hasGDPRCookie) {
                return true;
            }
        }
    }

    // Care about none disclaimer can skip splash screen when don't pass human event
    if (isPasswordRequired && hasPwdInCookie && isNoneDisclaimer) {
        return true;
    }

    if (!isPasswordRequired && isNoneDisclaimer) {
        return true;
    }

    return false;
};

export const getCustomMessage = (gdprSettingDetail, defaultTranslation, viewerLocation) => {
    let customMessage = {};
    const countryCode = (viewerLocation && viewerLocation.countryCode) || '';

    if (countryCode.toUpperCase() === 'US') {
        customMessage = gdprSettingDetail.us || gdprSettingDetail.US || {
            consentLocation: '',
            disclaimerMessage: '',
            privacyLink: ''
        };
    } else if (!EUCountryList.some(location => location.toUpperCase() === countryCode.toUpperCase())) {
        customMessage = { ...(gdprSettingDetail[defaultTranslation] || {}) };
    } else {
        const browserLang = getBrowserLang();
        const lang = gdprSettingDetail ?
            (Object.keys(gdprSettingDetail).find(l => l.toLowerCase() === browserLang.toLowerCase()) ||
                Object.keys(gdprSettingDetail).find(l => l.toLowerCase() === browserLang.substring(0, 2).toLowerCase()) ||
                defaultTranslation) : '';
        if (lang) {
            customMessage = { ...(gdprSettingDetail[lang] || {}) };
        }
    }

    return { ...customMessage, useDefaultMessage: false };
};

export const getGlobalGDPRDetail = (privacySetting) => {
    const { defaultLanguage, currentCountry, isEnable } = privacySetting;

    let disclaimerMessage = {};
    if (currentCountry) {
        const { disclaimerLibrary } = currentCountry;
        const { type, translations = [] } = disclaimerLibrary;

        if (type !== DISCLAIMER_TYPE.NONE_DISCLAIMER) {
            const browserLang = getBrowserLang();
            disclaimerMessage = translations.find(l => l.languageCode.toLowerCase() === browserLang.toLowerCase());

            if (!disclaimerMessage) {
                disclaimerMessage = translations.find(l => l.languageCode.substring(0, 2).toLowerCase() === browserLang.substring(0, 2).toLowerCase());
            }

            if (!disclaimerMessage) {
                disclaimerMessage = translations.find(l => l.languageCode.toLowerCase() === defaultLanguage.toLowerCase() ||
                l.languageCode.substring(0, 2).toLowerCase() === defaultLanguage.toLowerCase());
            }
        }

        return {
            isShowPrivacySettingBtn: type === DISCLAIMER_TYPE.WITH_PRIVACY_SETTING || type === DISCLAIMER_TYPE.WITH_REJECT_ALL,
            isNoDisclaimer: type === DISCLAIMER_TYPE.NONE_DISCLAIMER,
            disclaimerMessage: (disclaimerMessage && disclaimerMessage.message) || '',
            isShowDisclaimerMessage: isEnable && type !== DISCLAIMER_TYPE.NONE_DISCLAIMER,
            isShowRejectAllBtn: type === DISCLAIMER_TYPE.WITH_REJECT_ALL
        };
    }

    return {
        isShowPrivacySettingBtn: false,
        isNoDisclaimer: false,
        disclaimerMessage: '',
        isShowDisclaimerMessage: false,
        isShowRejectAllBtn: false
    };
};

export const isOnlyFunctionalCookies = isWatermarkEnabled => getGDPRCookie(isWatermarkEnabled) === ConsentGDPRCookieValue.ONLY_FUNCTIONAL_COOKIES;

export const isAllTracking = isWatermarkEnabled => getGDPRCookie(isWatermarkEnabled) === ConsentGDPRCookieValue.ALL_TRACKING_AND_COOKIES;
